import { computed, Injector, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, OperatorFunction, pipe, startWith, Subject, switchMap } from 'rxjs';

export type AsyncState<T> = { status: 'loading' } | { status: 'error' } | { status: 'ok'; data: T };

export function mapAsyncState<TIn, TOut>(state: AsyncState<TIn>, project: (data: TIn) => TOut): AsyncState<TOut> {
    return state.status === 'ok' ? { status: 'ok', data: project(state.data) } : state;
}

/** Use inside a `switchMap` so every re-fetch emits `loading` again. */
export function mapToAsyncState<T>(): OperatorFunction<T, AsyncState<T>> {
    return pipe(
        map((data): AsyncState<T> => ({ status: 'ok', data })),
        catchError((): Observable<AsyncState<T>> => of({ status: 'error' })),
        startWith<AsyncState<T>>({ status: 'loading' })
    );
}

/** Must run in an injection context. */
export function toAsyncStateSignal<T>(source$: Observable<T>): Signal<AsyncState<T>> {
    return createAsyncResource(() => source$).state;
}

export interface AsyncResource<T> {
    readonly state: Signal<AsyncState<T>>;
    /** `undefined` while loading and after a failure — a refetch discards the previous payload. */
    readonly data: Signal<T | undefined>;
    readonly loading: Signal<boolean>;
    readonly error: Signal<boolean>;
    /** A fetch has been started, whether or not it has finished. */
    readonly requested: Signal<boolean>;
    /** Fetches on the first call only. */
    load(): void;
    reload(): void;
}

export interface AsyncResourceOptions {
    /** Required when the resource is not created inside an injection context. */
    injector?: Injector;
    /** Skips the fetch at construction, so the first `load()` starts it instead. */
    lazy?: boolean;
}

/** A parameterized request is expressed by closing over the signals it depends on and re-running via `reload()`. */
export function createAsyncResource<T>(fetch: () => Observable<T>, options?: AsyncResourceOptions): AsyncResource<T> {
    const fetchTrigger = new Subject<void>();
    const requested = signal(!options?.lazy);

    const trigger$: Observable<unknown> = options?.lazy ? fetchTrigger : fetchTrigger.pipe(startWith(undefined));

    const state = toSignal(trigger$.pipe(switchMap(() => fetch().pipe(mapToAsyncState()))), {
        initialValue: { status: 'loading' } as AsyncState<T>,
        injector: options?.injector
    });

    return {
        state,
        data: computed(() => {
            const current = state();
            return current.status === 'ok' ? current.data : undefined;
        }),
        loading: computed(() => requested() && state().status === 'loading'),
        error: computed(() => state().status === 'error'),
        requested: requested.asReadonly(),
        load: () => {
            if (requested()) {
                return;
            }
            requested.set(true);
            fetchTrigger.next();
        },
        reload: () => {
            requested.set(true);
            fetchTrigger.next();
        }
    };
}
